<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-input
              v-model="searchCode"
              :placeholder="$t('attributes.search') + $t('attributes.code')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.country_template') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="code"
        :label="$t('attributes.code')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.code }}</p>
      </b-table-column>

      <b-table-column
        field="flag"
        :label="$t('attributes.country_flag')"
        width="80"
        sortable
        centered
        v-slot="props"
      >
        <figure v-if="props.row.flag" class="image">
          <img
            :src="countryTemplateFlagImage(props.row.flag)"
            :alt="$t('attributes.country_flag')"
            style="width: 50px; margin: auto;"
          />
        </figure>
        <p v-else class="has-text-danger has-text-weight-medium">無</p>
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        field="english_name"
        :label="$t('attributes.english_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.english_name }}
      </b-table-column>

      <b-table-column
        :width="120"
        field="continent_name"
        :label="$t('attributes.continent_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.continent_name }}
      </b-table-column>

      <b-table-column :width="80" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <country-template-form :country-template="selectedItem" @send="onSend" />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { COUNTRY_TEMPLATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import CountryTemplateForm from './CountryTemplateForm'

export default {
  name: 'CountryTemplateList',
  components: { CountryTemplateForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      searchName: this.query.search || '',
      searchCode: this.query.code || '',
      sortField: this.query.sort || 'code',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      isFormModalActive: false,
      sending: false
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${COUNTRY_TEMPLATES_API}`
        const params = {
          search_code: this.searchCode,
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('country_template', e)
      } finally {
        this.loadingItems = false
      }
    },
    updateQuery() {
      const query = {
        code: this.searchCode,
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilter: debounce(function() {
      this.updateQuery()
      this.loadItems()
    }, 500),
    onPageChange(page) {
      this.page = page

      this.updateQuery()
      this.loadItems()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
      this.loadItems()
    },

    onAdd() {
      this.selectedItem = {
        code: null,
        name: '',
        english_name: '',
        flag: ''
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.code == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(COUNTRY_TEMPLATES_API, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${COUNTRY_TEMPLATES_API}/${item.code}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.code === _item.code)
        const items = [...this.items]
        items[idx] = _item
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
